'use client';

import { useRef, useState } from 'react';
import { solveSudoku, validGrid, type Grid } from '@/lib/browser-word-solvers';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

const EXAMPLE = '530070000600195000098000060800060003400803001700020006060000280000419005000080079';

export default function BrowserSudokuSolver({ config }: { config?: { toolSlug?: string } }) {
  const [grid, setGrid] = useState<Grid>(() => EXAMPLE.split('').map(Number));
  const [given, setGiven] = useState<boolean[]>(() => EXAMPLE.split('').map((c) => c !== '0'));
  const [message, setMessage] = useState<string | null>(null);
  const [bad, setBad] = useState<number[]>([]);
  const reported = useRef(false);

  function set(i: number, raw: string) {
    const v = Number(raw.replace(/[^1-9]/g, '').slice(-1)) || 0;
    setGrid((g) => g.map((x, j) => (j === i ? v : x)));
    setGiven((g) => g.map((x, j) => (j === i ? v > 0 : x)));
    setMessage(null);
    setBad([]);
  }

  function solve() {
    if (!reported.current) {
      reported.current = true;
      void consumeToolUsage(config?.toolSlug ?? 'sudoku-solver', '').catch(() => undefined);
    }
    const puzzle = grid.map((v, i) => (given[i] ? v : 0));
    const check = validGrid(puzzle);
    if (!check.ok) {
      setBad(check.conflict ?? []);
      setMessage('Two highlighted cells break the rules (same number in a row, column or box).');
      return;
    }
    if (puzzle.filter(Boolean).length < 17) {
      setMessage('A sudoku needs at least 17 clues to have a single solution; add more numbers.');
    }
    const { solution, solutions } = solveSudoku(puzzle);
    if (!solution) {
      setMessage('No solution: these clues contradict each other further down the line.');
      return;
    }
    setGrid(solution);
    setMessage(
      solutions > 1
        ? 'Solved, but this puzzle has more than one solution; this is one of them.'
        : 'Solved. This puzzle has exactly one solution.',
    );
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Sudoku</span>
        <h2 className="tool-live-title">Sudoku solver</h2>
        <p className="tool-live-subtitle">
          Type the clues, press Solve. Solving runs in your browser in milliseconds.
        </p>
      </div>
      <div className="tool-live-grid">
        <section className="tool-live-preview">
          <div
            role="grid"
            aria-label="Sudoku grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(9, 1fr)',
              maxWidth: 400,
              aspectRatio: '1',
              border: '2px solid currentColor',
            }}
          >
            {grid.map((v, i) => {
              const r = Math.floor(i / 9);
              const c = i % 9;
              return (
                <input
                  aria-label={`Row ${r + 1}, column ${c + 1}`}
                  inputMode="numeric"
                  key={i}
                  onChange={(e) => set(i, e.target.value)}
                  style={{
                    width: '100%',
                    height: '100%',
                    textAlign: 'center',
                    fontSize: 'clamp(1rem, 4vw, 1.4rem)',
                    fontWeight: given[i] ? 700 : 400,
                    color: given[i] ? 'inherit' : 'var(--accent, #2563eb)',
                    background: bad.includes(i) ? 'rgba(220,38,38,0.2)' : 'transparent',
                    border: 0,
                    borderRight:
                      c % 3 === 2 && c < 8
                        ? '2px solid currentColor'
                        : '1px solid rgba(127,127,127,0.4)',
                    borderBottom:
                      r % 3 === 2 && r < 8
                        ? '2px solid currentColor'
                        : '1px solid rgba(127,127,127,0.4)',
                    padding: 0,
                    minWidth: 0,
                  }}
                  type="text"
                  value={v || ''}
                />
              );
            })}
          </div>
        </section>
        <section className="tool-live-uploader">
          <div className="tool-live-actions">
            <button className="button" onClick={solve} type="button">
              Solve
            </button>
            <button
              className="button button-secondary"
              onClick={() => {
                setGrid(grid.map((v, i) => (given[i] ? v : 0)));
                setMessage(null);
              }}
              type="button"
            >
              Clear answers
            </button>
            <button
              className="button button-secondary"
              onClick={() => {
                setGrid(Array(81).fill(0));
                setGiven(Array(81).fill(false));
                setMessage(null);
                setBad([]);
              }}
              type="button"
            >
              Empty grid
            </button>
          </div>
          {message ? <p className="tool-live-panel-note">{message}</p> : null}
          <p className="tool-live-panel-note">
            Clues are bold; the solver&apos;s numbers are in the accent colour.
          </p>
        </section>
      </div>
    </div>
  );
}
